import { usePersistedState } from "@/lib/persistedFilters";
// Productivity app — monitoring-tool reporting (tracked roster only).
// Roles: owner (Dan/Keith/super admin) manage access; admin manage
// classifications + daily report; viewer read-only. Measured against a
// 7.5h/day productive goal. Data comes from the hourly monitoring-tool cache.

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  BarChart3, RefreshCw, Loader2, ShieldAlert, FileDown, Monitor, Globe, CalendarDays,
  Wand2, Mail, Flame, ExternalLink, Users as UsersIcon, Video, Columns3, ArrowUp, ArrowDown, ChevronsUpDown, Merge,
} from "lucide-react";
import { ProdGoalsCard } from "@/components/ProdGoalsCard";
import { ProdAppNudgeCard } from "@/components/ProdAppNudgeCard";
import { ProdAppNamingCard } from "@/components/ProdAppNamingCard";
import { ProdDipCard } from "@/components/ProdDipCard";
import { ProdRosterCard } from "@/components/ProdRosterCard";
import { ProdWindowCard } from "@/components/ProdWindowCard";
import { ProdAlertsPanel } from "@/components/ProdAlertsPanel";
import { ProdMonthlyPanel } from "@/components/ProdMonthlyPanel";
import { ProdComparePanel } from "@/components/ProdComparePanel";
import { ProdAppMergeDialog } from "@/components/ProdAppMergeDialog";
import ProdDayTimeline from "@/components/ProdDayTimeline";
import ProdTimecard from "@/components/ProdTimecard";
import { DayBandChart } from "@/components/prodTimelineShared";
import { MetricLegend } from "@/components/MetricLegend";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, Legend, ReferenceLine, Cell, LabelList,
} from "recharts";

const CLS_TONE = {
  productive: "bg-emerald-100 text-emerald-800 dark:text-emerald-200",
  unproductive: "bg-rose-100 text-rose-800 dark:text-rose-200",
  neutral: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300",
  unclassified: "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300",
};
const hrs = (s) => Math.round((s / 3600) * 10) / 10;

// Optional columns for the Users table (name/select/actions are always shown).
const USER_COLUMNS = [
  { id: "team", label: "Team", sortKey: "team", align: "left" },
  { id: "tmwork", label: "Total work time", sortKey: "work_seconds", align: "right", title: "The monitoring tool's Time Cards \u2192 Total work time: the raw machine total, idle included and no working-window cap." },
  { id: "recorded", label: "Online", sortKey: "seconds", align: "right", title: "Time the machine was online (always-on Macs can read ~24h/day). Active % shows real input time." },
  { id: "productive", label: "Productive", sortKey: "productive_seconds", align: "right" },
  { id: "avgprod", label: "Avg prod/day", sortKey: "avg_productive_seconds", align: "right" },
  { id: "goal", label: "Hrs %", sortKey: "goal_pct", align: "right", width: "w-20 whitespace-nowrap", title: "Hours completed: average productive hours/day against the target for the day." },
  { id: "trend", label: "Trend", align: "right", width: "w-[248px]", title: "Productive hours across the selected period. Weekends are left out of the trend." },
  { id: "topapp", label: "Top app/site", sortKey: "top_app", align: "left" },
];
const USER_COL_IDS = USER_COLUMNS.map((c) => c.id);

function ClsBadge({ cls }) {
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CLS_TONE[cls] || CLS_TONE.unclassified}`}>{cls}</span>;
}

function ClassifySelect({ value, onChange, testid, disabled }) {
  if (disabled) return <ClsBadge cls={value} />;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className={`rounded border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-xs ${CLS_TONE[value] || ""}`}
      data-testid={testid}>
      <option value="unclassified">unclassified</option>
      <option value="productive">productive</option>
      <option value="unproductive">unproductive</option>
      <option value="neutral">neutral</option>
    </select>
  );
}

function GoalPct({ pct }) {
  const tone = pct >= 100 ? "text-emerald-700" : pct >= 70 ? "text-amber-600" : "text-rose-600";
  return <span className={`font-semibold ${tone}`}>{pct}%</span>;
}

function Sparkline({ data = [], labels = [], testid = "prod-sparkline" }) {
  const [hover, setHover] = useState(null);
  if (!data.length) return <span className="text-slate-300 dark:text-slate-500">—</span>;
  // A single bucket (e.g. Today) has no trajectory — show the figure instead.
  if (data.length === 1) {
    return <span className="tabular-nums text-slate-600 dark:text-slate-300"
      title={labels[0] ? `${labels[0]} · ${data[0]}h productive` : undefined}
      data-testid={testid}>{data[0]}h</span>;
  }
  const max = Math.max(...data, 1);
  const w = 174, h = 16, step = w / (data.length - 1);
  const xy = (v, i) => [i * step, h - (v / max) * h];
  const pts = data.map((v, i) => xy(v, i).map((n) => n.toFixed(1)).join(",")).join(" ");
  const up = data[data.length - 1] >= data[0];
  const colour = up ? "#16a34a" : "#e11d48";
  return (
    <span className="relative inline-block align-middle" onMouseLeave={() => setHover(null)}>
      <svg width={w} height={h + 6} className="block overflow-visible" data-testid={testid}>
        <polyline points={pts} fill="none" stroke={colour} strokeWidth="1.5" strokeLinejoin="round" />
        {data.map((v, i) => {
          const [cx, cy] = xy(v, i);
          return (
            <g key={i}>
              <circle cx={cx.toFixed(1)} cy={cy.toFixed(1)} r={hover === i ? 2.6 : 1.3} fill={colour} />
              <rect x={(cx - step / 2).toFixed(1)} y="0" width={step.toFixed(1)} height={h + 6}
                fill="transparent" style={{ cursor: "pointer" }}
                onMouseEnter={() => setHover(i)}
                data-testid={`${testid}-pt-${i}`}>
                <title>{`${labels[i] || `#${i + 1}`} · ${v}h productive`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[11px] font-medium text-white shadow-lg dark:bg-slate-700"
          data-testid={`${testid}-tip`}>
          {labels[hover] ? `${labels[hover]} · ` : ""}{data[hover]}h
        </span>
      )}
    </span>
  );
}

function DipBadge({ row }) {
  if (!row?.dip) return null;
  const win = (row.dip_labels || []).join(" → ");
  return (
    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-rose-50 dark:bg-rose-950/40 px-1 py-0.5 align-middle text-[10px] font-semibold text-rose-700 dark:text-rose-300"
      title={`Fell two buckets in a row: ${row.dip_note}${win ? ` (${win})` : ""}`}
      data-testid="prod-dip-badge">
      <ArrowDown className="h-2.5 w-2.5" />{row.dip_drop}h
    </span>
  );
}

function TimecardBar({ row, goalSeconds = 27000 }) {
  const total = row.work_seconds || 1;
  const seg = (s, color) => (s > 0 ? <div style={{ width: `${(s * 100) / total}%`, background: color }} className="h-full" /> : null);
  const markerLeft = Math.min(100, (goalSeconds * 100) / total);
  const hit = row.productive_seconds >= goalSeconds;
  return (
    <div className="relative" title={`7.5h goal ${hit ? "met ✓" : "not met"}`}>
      <div className="flex h-3 w-full overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
        {seg(row.productive_seconds, "#16a34a")}
        {seg(row.unproductive_seconds, "#e11d48")}
        {seg(Math.max(0, row.work_seconds - row.productive_seconds - row.unproductive_seconds - row.idle_seconds), "#f59e0b")}
        {seg(row.idle_seconds, "#cbd5e1")}
      </div>
      <div className="absolute -top-1 -bottom-1 w-0.5 bg-rose-600" style={{ left: `${markerLeft}%` }} data-testid={`prod-goal-marker-${row.date}`} />
    </div>
  );
}

// Charts sit on a themed card, so the tooltip has to follow the theme too.
const isDarkMode = () => document.documentElement.classList.contains("dark");
// CSS-driven so ticks stay legible when the theme flips without a re-render.
const axisTick = () => ({ className: "fill-slate-700 dark:fill-slate-200", fontSize: 11, fontWeight: 500 });
const tooltipStyle = () => (isDarkMode()
  ? { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }
  : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#0f172a" });

// Today first, then the wider windows. `1` renders as "Today".
const RANGE_PRESETS = [1, 7, 14, 30];

function Stat({ label, value, sub, testid }) {
  return (
    <Card className="p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-xl font-bold text-slate-900 dark:text-slate-50">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
    </Card>
  );
}

export default function Productivity() {
  const today = new Date().toISOString().slice(0, 10);

  const [access, setAccess] = useState(null);
  // Opens on today; the presets widen it out.
  // Landing view: Teams over the last 30 days.
  const [range, setRange] = useState({
    from: new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10), to: today,
  });
  // Hand-picked days (ISO strings, sorted). When set, they replace the range.
  const [pickedDays, setPickedDays] = useState([]);
  const loadToken = useRef(0);
  const [params, setParams] = useSearchParams();
  // Deep links from the dashboard: /productivity?tab=users&team=Engineering
  const [tab, setTab] = useState(() => params.get("tab") || "teams");
  const [teamFilter, setTeamFilter] = usePersistedState("productivity.team", "", params.get("team"));
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [trendMeta, setTrendMeta] = useState(null);
  const [dipOnly, setDipOnly] = useState(false);
  const [teams, setTeams] = useState([]);
  const [apps, setApps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reportCfg, setReportCfg] = useState(null);
  const [recipInput, setRecipInput] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [acl, setAcl] = useState(null);
  const [adminInput, setAdminInput] = useState("");
  const [viewerInput, setViewerInput] = useState("");
  const [aclBusy, setAclBusy] = useState(false);
  const [idleCfg, setIdleCfg] = useState(null);
  const [idleBusy, setIdleBusy] = useState(false);
  const [weeklyCfg, setWeeklyCfg] = useState(null);
  const [weeklyRecip, setWeeklyRecip] = useState("");
  const [weeklyBusy, setWeeklyBusy] = useState(false);
  const [itemSort, setItemSort] = useState("time");
  const [dayOpen, setDayOpen] = useState(null);
  const [dayApps, setDayApps] = useState([]);
  const [dayBands, setDayBands] = useState(null);
  const [dayBandWindow, setDayBandWindow] = useState(null);
  const [dayLongest, setDayLongest] = useState(null);
  const [dayBusy, setDayBusy] = useState(false);
  const [dayTag, setDayTag] = useState("");
  const [dayNote, setDayNote] = useState("");
  const [selectedApps, setSelectedApps] = useState([]);
  const [appsSummary, setAppsSummary] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("prod_user_cols"));
      // New columns default to visible even for people with saved preferences.
      if (Array.isArray(s)) return USER_COL_IDS.filter((c) => s.includes(c) || c === "tmwork");
    } catch { /* noop */ }
    return USER_COL_IDS;
  });
  const [sortBy, setSortBy] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("prod_user_sort")); if (s?.key) return s; } catch { /* noop */ }
    return { key: "seconds", dir: "desc" };
  });

  const role = access?.role;
  const isManager = role === "owner" || role === "admin";
  const isOwner = role === "owner";
  const teamQ = teamFilter ? `&team=${encodeURIComponent(teamFilter)}` : "";
  const baseQ = (pickedDays.length ? `days=${pickedDays.join(",")}` : `from=${range.from}&to=${range.to}`) + teamQ;
  const scopeQ = `${baseQ}${selected.length ? `&keys=${selected.join(",")}` : ""}`;

  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setLoading(true);
    try {
      const [o, u, t, a] = await Promise.all([
        api.get(`/productivity/overview?${scopeQ}`),
        api.get(`/productivity/users?${baseQ}`),
        api.get(`/productivity/teams?${scopeQ}`),
        api.get(`/productivity/apps?${scopeQ}`),
      ]);
      // A slower earlier request must never overwrite a newer selection.
      if (token !== loadToken.current) return;
      setOv(o.data); setUsers(u.data.users); setTrendMeta(u.data.trend); setTeams(t.data.teams); setApps(a.data.apps);
      setAppsSummary(a.data.summary || null);
    } catch (e) {
      if (token === loadToken.current) toast.error(e.response?.data?.detail || "Load failed");
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  }, [baseQ, scopeQ]);

  useEffect(() => {
    api.get("/productivity/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ allowed: false }));
  }, []);
  useEffect(() => { if (access?.allowed) load(); }, [access, load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/productivity/sync", { days: 7 });
      if (data.queued) {
        toast.info(`Backfilling ${data.days} days in the background — this can take a couple of minutes.`);
      } else if (data.auth_failed) {
        toast.error("The productivity tool rejected our API token — it needs replacing in the backend settings.",
                    { duration: 10000 });
      } else if (data.error) toast.error(`Sync error: ${data.error}`, { duration: 10000 });
      else toast.success(`Synced ${data.activity_rows} rows (${data.agents} people)`);
      // Refresh the "last sync" stamp too, not just the figures.
      api.get("/productivity/access").then(({ data: d }) => setAccess(d)).catch(() => {});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.detail || "Sync failed",
                  { duration: 10000 });
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (key) => {
    setDayOpen(null); setDayApps([]);
    const { data } = await api.get(`/productivity/users/${key}?${pickedDays.length ? `days=${pickedDays.join(",")}` : `from=${range.from}&to=${range.to}`}`);
    setDetail(data);
  };

  const openDay = async (dateStr) => {
    if (dayOpen === dateStr) { setDayOpen(null); return; }
    setDayOpen(dateStr); setDayBusy(true); setDayApps([]); setDayTag(""); setDayNote("");
    setDayBands(null); setDayBandWindow(null); setDayLongest(null);
    try {
      const { data } = await api.get(`/productivity/users/${detail.key}/day?date=${dateStr}`);
      setDayApps(data.apps); setDayTag(data.tag || ""); setDayNote(data.note || "");
      setDayBands(data.bands || null); setDayBandWindow(data.band_window || null);
      setDayLongest(data.longest_idle || null);
    } catch {
      toast.error("Could not load that day");
    } finally { setDayBusy(false); }
  };

  const saveDayNote = async () => {
    try {
      await api.post(`/productivity/users/${detail.key}/day/note`, { date: dayOpen, tag: dayTag, note: dayNote });
      setDetail((d) => ({ ...d, timecard: d.timecard.map((r) => (r.date === dayOpen ? { ...r, tag: dayTag, note: dayNote } : r)) }));
      toast.success("Day tagged");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const classify = async (name, cls) => {
    await api.post("/productivity/apps/classify", { name, cls });
    setApps((prev) => prev.map((a) => (a.name === name ? { ...a, cls } : a)));
    setOv((prev) => prev ? { ...prev, top_apps: prev.top_apps.map((a) => (a.name === name ? { ...a, cls } : a)) } : prev);
    toast.success(`${name} → ${cls}`);
  };

  const classifySelectedApps = async (cls) => {
    if (!selectedApps.length) return;
    try {
      const { data } = await api.post("/productivity/apps/classify-bulk", { names: selectedApps, cls });
      toast.success(`${data.count} selected → ${cls}`);
      setSelectedApps([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk classify failed");
    }
  };
  const toggleApp = (name) => setSelectedApps((s) => (s.includes(name) ? s.filter((n) => n !== name) : [...s, name]));

  const CLS_RANK = { productive: 0, unproductive: 1, neutral: 2, unclassified: 3 };
  const sortedItems = [...(detail?.top_items || [])].sort((a, b) => {
    if (itemSort === "name") return a.name.localeCompare(b.name);
    if (itemSort === "cls") return (CLS_RANK[a.cls] - CLS_RANK[b.cls]) || (b.seconds - a.seconds);
    return b.seconds - a.seconds;   // time / % (proportional)
  });

  const doBulkClassify = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/productivity/apps/bulk-classify?${baseQ}`, { limit: 50, mode: "unclassified" });
      if (!data.attempted) toast.info(data.message || "Everything is already classified.");
      else toast.success(`AI classified ${data.classified} of ${data.attempted} apps & sites`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk classify failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const fetchPdf = async (query) => {
    const res = await fetch(`${API}/productivity/report/pdf?${query}`, { credentials: "include" });
    if (!res.ok) { toast.error("PDF export failed"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = pickedDays.length
      ? `productivity-${pickedDays.length}-picked-days.pdf`
      : `productivity-${range.from}-to-${range.to}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const exportView = () => fetchPdf(scopeQ);
  const downloadUserPdf = (key) => fetchPdf(`${baseQ}&keys=${key}`);
  const reportSelected = () => { if (selected.length) fetchPdf(`${baseQ}&keys=${selected.join(",")}`); };

  const toggleSel = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const toggleCol = (id) => setVisibleCols((cols) => {
    const next = cols.includes(id) ? cols.filter((c) => c !== id) : USER_COL_IDS.filter((c) => c === id || cols.includes(c));
    localStorage.setItem("prod_user_cols", JSON.stringify(next));
    return next;
  });
  const setSort = (key) => setSortBy((s) => {
    const next = s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" };
    localStorage.setItem("prod_user_sort", JSON.stringify(next));
    return next;
  });
  const sortedUsers = [...users].filter((u) => !dipOnly || u.dip).sort((a, b) => {
    const av = a[sortBy.key], bv = b[sortBy.key];
    const cmp = typeof av === "string" ? String(av).localeCompare(String(bv))
      : (Number(av) || 0) - (Number(bv) || 0);
    return sortBy.dir === "asc" ? cmp : -cmp;
  });
  const cols = USER_COLUMNS.filter((c) => visibleCols.includes(c.id));
  const SortHead = ({ label, sortKey, align, title, width }) => (
    <th className={`px-3 py-2 ${width || ""} ${align === "right" ? "text-right" : ""} cursor-pointer select-none hover:text-slate-700`}
      onClick={() => setSort(sortKey)} title={title} data-testid={`prod-users-sort-${sortKey}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        {sortBy.key === sortKey ? (sortBy.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </th>
  );
  const userCell = (u, id) => {
    switch (id) {
      case "team": return u.team;
      case "tmwork": return <span className="font-mono text-xs">{u.work_hms}</span>;
      case "recorded": return <span className="font-mono text-xs">{u.hms}</span>;
      case "productive": return <span className="font-mono text-xs">{u.productive_hms}</span>;
      case "avgprod": return <span className="font-mono text-xs">{u.avg_productive_hms}</span>;
      case "goal": return <GoalPct pct={u.goal_pct} />;
      case "trend": return (
        <span className="inline-flex items-center justify-end">
          <Sparkline data={u.trend} labels={trendMeta?.bucket_labels || []} />
          <DipBadge row={u} />
        </span>
      );   // label comes from the range
      case "topapp": return <span className="text-xs">{u.top_app} <span className="text-slate-400 dark:text-slate-400">({u.top_app_hms})</span></span>;
      default: return null;
    }
  };

  // ---- settings loaders
  const loadReport = useCallback(async () => {
    const { data } = await api.get("/productivity/report/config");
    setReportCfg(data);
    setRecipInput((data.config.recipients || []).join(", "));
  }, []);
  const loadAcl = useCallback(async () => {
    const { data } = await api.get("/productivity/acl");
    setAcl(data);
    setAdminInput((data.admins || []).join(", "));
    setViewerInput((data.viewers || []).join(", "));
  }, []);
  const loadIdle = useCallback(async () => {
    const { data } = await api.get("/productivity/idle-allowance");
    setIdleCfg(data);
  }, []);
  const loadWeekly = useCallback(async () => {
    const { data } = await api.get("/productivity/weekly/config");
    setWeeklyCfg(data);
    setWeeklyRecip((data.config.recipients || []).join(", "));
  }, []);
  const openSettings = () => { loadReport(); loadAcl(); loadIdle(); loadWeekly(); };

  const saveIdle = async () => {
    setIdleBusy(true);
    try {
      const { data } = await api.put("/productivity/idle-allowance", { minutes: idleCfg.minutes });
      setIdleCfg(data);
      toast.success("Idle allowance saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setIdleBusy(false); }
  };
  const saveWeekly = async () => {
    setWeeklyBusy(true);
    try {
      const { data } = await api.put("/productivity/weekly/config", { enabled: weeklyCfg.config.enabled, recipients: weeklyRecip });
      setWeeklyCfg((c) => ({ ...c, config: data.config }));
      setWeeklyRecip((data.config.recipients || []).join(", "));
      toast.success("Weekly digest settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setWeeklyBusy(false); }
  };
  const sendWeeklyNow = async () => {
    setWeeklyBusy(true);
    try {
      const { data } = await api.post("/productivity/weekly/send-now");
      if (data.status === "sent") toast.success(`Weekly digest emailed to ${data.sent} recipient(s)`);
      else if (data.status === "paused") toast.warning("Built, but outbound email is paused — not delivered");
      else if (data.status === "no_recipients") toast.error("Add at least one recipient first");
      else toast.error(`Send failed (${data.status})`);
      await loadWeekly();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setWeeklyBusy(false); }
  };

  const saveReport = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.put("/productivity/report/config", {
        enabled: reportCfg.config.enabled, recipients: recipInput,
        days: reportCfg.config.days, team: reportCfg.config.team,
      });
      setReportCfg((c) => ({ ...c, config: data.config }));
      setRecipInput((data.config.recipients || []).join(", "));
      toast.success("Daily report settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setReportBusy(false); }
  };

  const sendReportNow = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.post("/productivity/report/send-now");
      if (data.status === "sent") toast.success(`Report emailed to ${data.sent} recipient(s)`);
      else if (data.status === "paused") toast.warning(`Built, but outbound email is paused — not delivered`);
      else if (data.status === "no_recipients") toast.error("Add at least one recipient first");
      else toast.error(`Send failed (${data.status})`);
      await loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setReportBusy(false); }
  };

  const saveAcl = async () => {
    setAclBusy(true);
    try {
      const { data } = await api.put("/productivity/acl", { admins: adminInput, viewers: viewerInput });
      setAcl((a) => ({ ...a, ...data }));
      setAdminInput((data.admins || []).join(", "));
      setViewerInput((data.viewers || []).join(", "));
      toast.success("Access list saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setAclBusy(false); }
  };

  if (access === null) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 dark:text-slate-400" /></div>;
  if (!access.allowed) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 text-center" data-testid="productivity-denied">
        <ShieldAlert className="mx-auto h-10 w-10 text-rose-500" />
        <div className="mt-3 text-lg font-semibold text-slate-800 dark:text-slate-100">Productivity is restricted</div>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Ask Dan or Keith to add you in the Productivity app's Settings tab.</p>
      </div>
    );
  }

  const t = ov?.totals;
  const preset = (days) => {
    setPickedDays([]);
    setRange({ from: new Date(Date.now() - (days - 1) * 864e5).toISOString().slice(0, 10), to: today });
  };
  const togglePicked = (iso) => setPickedDays((d) => (d.includes(iso) ? d.filter((x) => x !== iso) : [...d, iso].sort()));
  // Which preset the current range matches, so it can show as chosen.
  const activePreset = pickedDays.length ? 0 : (range.to === today
    ? Math.round((new Date(range.to) - new Date(range.from)) / 864e5) + 1
    : 0);
  const teamOptions = ["Service Delivery", "Engineering", "Admin"];

  return (
    <div className="space-y-4" data-testid="productivity-page">
      <PageHeader icon={BarChart3} title="Productivity" subtitle="Tracked-team activity vs a 7.5h/day productive goal — cached hourly." />

      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={range.from} max={range.to} disabled={pickedDays.length > 0}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm disabled:opacity-40 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" data-testid="prod-from-input" />
        <span className="text-slate-400 dark:text-slate-400">→</span>
        <input type="date" value={range.to} min={range.from} max={today} disabled={pickedDays.length > 0}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm disabled:opacity-40 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" data-testid="prod-to-input" />
        {RANGE_PRESETS.map((d) => (
          <Button key={d} size="sm" variant={activePreset === d ? "default" : "outline"}
            onClick={() => preset(d)} data-testid={d === 1 ? "prod-preset-today" : `prod-preset-${d}`}>
            {d === 1 ? "Today" : `${d}d`}
          </Button>
        ))}
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant={pickedDays.length ? "default" : "outline"} data-testid="prod-pick-days-btn">
              <CalendarDays className="mr-1 h-4 w-4" />
              {pickedDays.length ? `${pickedDays.length} picked ${pickedDays.length === 1 ? "day" : "days"}` : "Pick days"}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0" data-testid="prod-pick-days-popover">
            <div className="border-b border-slate-100 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
              Click any past days to compare just those — they don&apos;t have to be next to each other.
            </div>
            <Calendar mode="multiple" numberOfMonths={2} showOutsideDays={false}
              defaultMonth={new Date(range.to)}
              selected={pickedDays.map((d) => new Date(`${d}T12:00:00`))}
              disabled={{ after: new Date(`${today}T12:00:00`) }}
              onDayClick={(d) => togglePicked(
                new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10))} />
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2 dark:border-slate-800">
              <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="prod-picked-count">
                {pickedDays.length ? pickedDays.join(", ") : "Nothing picked — the range above is used"}
              </span>
              <Button size="sm" variant="ghost" disabled={!pickedDays.length}
                onClick={() => setPickedDays([])} data-testid="prod-picked-clear">Clear</Button>
            </div>
          </PopoverContent>
        </Popover>
        <select value={teamFilter} onChange={(e) => { setTeamFilter(e.target.value); setParams({}, { replace: true }); }}
          className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm dark:bg-slate-900 dark:text-slate-100" data-testid="prod-team-filter">
          <option value="">All teams</option>
          {teamOptions.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={doSync} disabled={syncing || !isManager} data-testid="prod-sync-btn">
          {syncing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Refresh now
        </Button>
        <Button size="sm" onClick={exportView} data-testid="prod-export-view-btn">
          <FileDown className="mr-1 h-4 w-4" /> Export current view (PDF)
        </Button>
        {access.last_sync && (
          <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="prod-last-sync">
            last sync {access.last_sync.started_at?.slice(0, 16).replace("T", " ")} UTC {access.last_sync.error ? "· FAILED" : ""}
          </span>
        )}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 dark:text-slate-400" />}
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({}, { replace: true }); }}>
        <TabsList data-testid="prod-tabs" className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="prod-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="prod-tab-users">Users</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="prod-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="timecard" data-testid="prod-tab-timecard">Time card</TabsTrigger>
          <TabsTrigger value="teams" data-testid="prod-tab-teams">Teams</TabsTrigger>
          <TabsTrigger value="apps" data-testid="prod-tab-apps">Applications</TabsTrigger>
          <TabsTrigger value="targets" data-testid="prod-tab-targets">Targets</TabsTrigger>
          <TabsTrigger value="compare" data-testid="prod-tab-compare">Compare</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="prod-tab-monthly">Month-end</TabsTrigger>
          {isManager && <TabsTrigger value="settings" data-testid="prod-tab-settings" onClick={openSettings}>Settings</TabsTrigger>}
          <div className="ml-auto flex items-center pl-3"><MetricLegend /></div>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {(selected.length > 0 || teamFilter) && (
            <div className="flex items-center gap-2 rounded-md border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 px-3 py-1.5 text-xs text-sky-800 dark:text-sky-200" data-testid="prod-overview-scope">
              Showing {selected.length > 0 ? `${selected.length} selected user${selected.length > 1 ? "s" : ""}` : "all tracked staff"}
              {teamFilter ? ` · team: ${teamFilter}` : ""}
              {selected.length > 0 && <button type="button" className="underline" onClick={() => setSelected([])} data-testid="prod-overview-scope-clear">clear</button>}
            </div>
          )}
          {t && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              <Stat label="Online (machine)" value={t.hms} sub="active % = real input" testid="prod-stat-total" />
              <Stat label="Productive" value={`${hrs(t.productive_seconds)}h`} sub={t.seconds ? `${Math.round((t.productive_seconds * 100) / t.seconds)}%` : ""} testid="prod-stat-productive" />
              <Stat label="Unproductive" value={`${hrs(t.unproductive_seconds)}h`} sub={t.seconds ? `${Math.round((t.unproductive_seconds * 100) / t.seconds)}%` : ""} testid="prod-stat-unproductive" />
              <Stat label="Idle" value={`${hrs(t.idle_seconds)}h`} testid="prod-stat-idle" />
              <Stat label="Tracked staff" value={t.active_users} testid="prod-stat-users" />
              <Stat label="Apps / sites" value={t.apps_tracked} testid="prod-stat-apps" />
            </div>
          )}
          <Card className="p-4">
            <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Daily activity (hours)</div>
            <div className="h-64" data-testid="prod-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(ov?.daily || []).map((d) => ({ ...d, hours: hrs(d.total), productive: hrs(d.productive), unproductive: hrs(d.unproductive) }))}>
                  <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><RTooltip contentStyle={tooltipStyle()} labelStyle={{ color: isDarkMode() ? "#94a3b8" : "#475569" }} /><Legend />
                  <Bar dataKey="hours" name="Total" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="productive" name="Productive" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="unproductive" name="Unproductive" fill="#e11d48" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="p-4" data-testid="prod-team-compare">
            <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Hours completed by team (avg productive vs 7.5h/day)</div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={teams.map((t) => ({ team: t.team, goal: t.goal_pct }))} margin={{ left: 24, right: 24, top: 4, bottom: 4 }}>
                  <XAxis type="number" domain={[0, 120]} fontSize={11} unit="%" />
                  <YAxis type="category" dataKey="team" width={110} fontSize={11} />
                  <RTooltip contentStyle={tooltipStyle()} formatter={(v) => [`${v}%`, "hours completed"]} />
                  <ReferenceLine x={100} stroke="#0f172a" strokeDasharray="3 3" label={{ value: "goal", fontSize: 9, position: "top" }} />
                  <Bar dataKey="goal" radius={[0, 4, 4, 0]}>
                    {teams.map((t, i) => <Cell key={i} fill={t.goal_pct >= 100 ? "#16a34a" : t.goal_pct >= 70 ? "#f59e0b" : "#e11d48"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4">
              <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Top users</div>
              {(ov?.top_users || []).map((u) => (
                <button key={u.key} type="button" onClick={() => openDetail(u.key)}
                  className="flex w-full items-center justify-between border-b border-slate-100 dark:border-slate-800 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  data-testid={`prod-top-user-${u.key}`}>
                  <span>{u.name} <span className="text-xs text-slate-400 dark:text-slate-400">· {u.team}</span></span>
                  <span className="font-mono text-xs">{u.hms}</span>
                </button>
              ))}
            </Card>
            <Card className="p-4">
              <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Top apps &amp; sites</div>
              {(ov?.top_apps || []).map((a) => (
                <div key={`${a.name}-${a.is_app ? "app" : "site"}`} className="flex items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-800 py-1.5 text-sm">
                  <span className="truncate">{a.name}</span>
                  <div className="flex shrink-0 items-center gap-2">
                    <ClassifySelect value={a.cls} onChange={(c) => classify(a.name, c)} disabled={!isManager} testid={`prod-ov-classify-${a.name}`} />
                    <span className="font-mono text-xs">{a.hms}</span>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">{selected.length ? `${selected.length} selected` : "Select users for an ad-hoc report"}</span>
            <Button size="sm" variant="outline" disabled={!selected.length} onClick={reportSelected} data-testid="prod-report-selected-btn">
              <FileDown className="mr-1 h-4 w-4" /> Report selected (PDF)
            </Button>
            {selected.length > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="prod-clear-sel">Clear</Button>}
            {users.some((u) => u.dip) && (
              <button type="button" onClick={() => setDipOnly((v) => !v)}
                title="Productive hours fell two buckets in a row"
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${dipOnly
                  ? "border-rose-500 bg-rose-600 text-white"
                  : "border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-950/70"}`}
                data-testid="prod-dip-chip">
                <ArrowDown className="h-3 w-3" />
                {users.filter((u) => u.dip).length} dipping{dipOnly ? " · showing only" : ""}
              </button>
            )}
            <div className="ml-auto">
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="prod-columns-btn"><Columns3 className="mr-1 h-4 w-4" /> Columns</Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-52" data-testid="prod-columns-popover">
                  <div className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">Show columns</div>
                  <div className="space-y-1.5">
                    {USER_COLUMNS.map((c) => (
                      <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`prod-col-${c.id}`}>
                        <Checkbox checked={visibleCols.includes(c.id)} onCheckedChange={() => toggleCol(c.id)} />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Card className="overflow-auto p-0">
            <table className="w-full text-left text-sm" data-testid="prod-users-table">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <SortHead label="User" sortKey="name" align="left" />
                  {cols.map((c) => (c.sortKey
                    ? <SortHead key={c.id} label={c.label} sortKey={c.sortKey} align={c.align} title={c.title} width={c.width} />
                    : <th key={c.id} className={`px-3 py-2 ${c.width || ""} ${c.align === "right" ? "text-right" : ""}`} title={c.title}>
                        {c.id === "trend" ? (trendMeta?.label || c.label) : c.label}
                      </th>))}
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => (
                  <tr key={u.key} className="cursor-pointer border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60" onClick={() => openDetail(u.key)} data-testid={`prod-user-row-${u.key}`}>
                    <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(u.key)} onCheckedChange={() => toggleSel(u.key)} data-testid={`prod-user-sel-${u.key}`} /></td>
                    <td className="px-3 py-1.5">{u.name}</td>
                    {cols.map((c) => <td key={c.id} className={`px-3 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>{userCell(u, c.id)}</td>)}
                    <td className="px-3 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => downloadUserPdf(u.key)} className="text-slate-500 dark:text-slate-400 hover:text-slate-800" title="User report (PDF)" data-testid={`prod-user-pdf-${u.key}`}><FileDown className="h-4 w-4" /></button>
                        <a href={u.portal_url} target="_blank" rel="noreferrer" className="text-slate-500 dark:text-slate-400 hover:text-slate-800" title="Open in Productivity App" data-testid={`prod-user-open-${u.key}`}><ExternalLink className="h-4 w-4" /></a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <ProdDayTimeline team={teamFilter} />
        </TabsContent>

        <TabsContent value="timecard">
          <ProdTimecard />
        </TabsContent>

        <TabsContent value="teams">
          <Card className="overflow-auto p-0">
            <table className="w-full text-left text-sm" data-testid="prod-teams-table">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Team</th><th className="px-3 py-2 text-right">Members</th><th className="px-3 py-2 text-right" title="Machine online time">Online</th><th className="px-3 py-2 text-right">Productive</th><th className="px-3 py-2 text-right">Productive %</th><th className="px-3 py-2 w-[248px] text-right" title="Team productive hours across the selected period.">{trendMeta?.label || "Trend"}</th></tr>
              </thead>
              <tbody>
                {teams.map((r) => (
                  <tr key={r.team} className="border-t border-slate-100 dark:border-slate-800" data-testid={`prod-team-row-${r.rank}`}>
                    <td className="px-3 py-1.5">{r.rank}</td>
                    <td className="px-3 py-1.5 font-medium">{r.team}</td>
                    <td className="px-3 py-1.5 text-right">{r.members}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{r.hms}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{r.productive_hms}</td>
                    <td className="px-3 py-1.5 text-right">{r.productive_pct === null ? "—" : `${r.productive_pct}%`}</td>
                    <td className="px-3 py-1.5 text-right">
                      <span className="inline-flex items-center justify-end">
                        <Sparkline data={r.trend} labels={trendMeta?.bucket_labels || []} testid={`prod-team-sparkline-${r.rank}`} />
                        <DipBadge row={r} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="apps">
          {appsSummary && (
            <div className="mb-3 grid gap-3 sm:grid-cols-4" data-testid="prod-apps-summary">
              <Stat label="Names tracked" value={appsSummary.names_total} testid="prod-apps-stat-names" />
              <Stat label="Unclassified time" value={appsSummary.unclassified_hms}
                sub={`${appsSummary.unclassified_pct}% of tracked time`} testid="prod-apps-stat-unclassified" />
              <Stat label="Names unclassified" value={appsSummary.names_unclassified}
                sub="the long tail" testid="prod-apps-stat-unclassified-names" />
              <Stat label="Merged names" value={appsSummary.merged_names}
                sub="rolled into one" testid="prod-apps-stat-merged" />
            </div>
          )}
          {isManager && (
            <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="prod-apps-toolbar">
              {selectedApps.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2" data-testid="prod-app-bulk-bar">
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{selectedApps.length} selected →</span>
                  <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => classifySelectedApps("productive")} data-testid="prod-bulk-productive">Productive</Button>
                  <Button size="sm" variant="outline" className="text-rose-700" onClick={() => classifySelectedApps("unproductive")} data-testid="prod-bulk-unproductive">Unproductive</Button>
                  <Button size="sm" variant="outline" onClick={() => classifySelectedApps("neutral")} data-testid="prod-bulk-neutral">Neutral</Button>
                  <Button size="sm" variant="outline" onClick={() => classifySelectedApps("unclassified")} data-testid="prod-bulk-unclassify">Clear</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedApps([])} data-testid="prod-bulk-cancel">Cancel</Button>
                </div>
              ) : (
                <span className="text-xs text-slate-500 dark:text-slate-400">Tick apps/sites to classify several at once.</span>
              )}
              <Button size="sm" variant="outline" onClick={() => setMergeOpen(true)} data-testid="prod-merge-open">
                <Merge className="mr-1 h-4 w-4" /> Merge duplicates
              </Button>
              <Button size="sm" onClick={doBulkClassify} disabled={bulkBusy} data-testid="prod-bulk-classify-btn">
                {bulkBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1 h-4 w-4" />} AI classify top 50 unclassified
              </Button>
            </div>
          )}
          <Card className="overflow-auto p-0">
            <table className="w-full text-left text-sm" data-testid="prod-apps-table">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  {isManager && <th className="px-3 py-2 w-8"></th>}
                  <th className="px-3 py-2">Application / website</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Time</th><th className="px-3 py-2 text-right">Users</th><th className="px-3 py-2">Classification</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={`${a.name}-${a.is_app ? "app" : "site"}`} className="border-t border-slate-100 dark:border-slate-800" data-testid={`prod-app-row-${a.name}`}>
                    {isManager && <td className="px-3 py-1.5"><Checkbox checked={selectedApps.includes(a.name)} onCheckedChange={() => toggleApp(a.name)} data-testid={`prod-app-sel-${a.name}`} /></td>}
                    <td className="px-3 py-1.5 max-w-md truncate">{a.name}</td>
                    <td className="px-3 py-1.5">{a.is_app ? <span className="inline-flex items-center gap-1 text-xs"><Monitor className="h-3 w-3" /> app</span> : <span className="inline-flex items-center gap-1 text-xs"><Globe className="h-3 w-3" /> website</span>}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{a.hms}</td>
                    <td className="px-3 py-1.5 text-right">{a.users}</td>
                    <td className="px-3 py-1.5"><ClassifySelect value={a.cls} onChange={(c) => classify(a.name, c)} disabled={!isManager} testid={`prod-classify-${a.name}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <ProdAppMergeDialog open={mergeOpen} onOpenChange={setMergeOpen}
            query={baseQ} onMerged={load} />
        </TabsContent>

        <TabsContent value="targets">
          <ProdAlertsPanel query={scopeQ} />
        </TabsContent>

        <TabsContent value="compare">
          <ProdComparePanel team={teamFilter}
            anchor={pickedDays.length ? pickedDays[pickedDays.length - 1] : range.to} />
        </TabsContent>

        <TabsContent value="monthly">
          <ProdMonthlyPanel canManage={isManager} />
        </TabsContent>

        {isManager && (
          <TabsContent value="settings" className="space-y-4">
            <ProdWindowCard canManage={isManager} />
            <ProdDipCard canManage={isManager} />
            <ProdGoalsCard canManage={isManager} />
            <ProdRosterCard canManage={isManager} />
            <ProdAppNamingCard canManage={isManager} />
            <ProdAppNudgeCard canManage={isManager} />
            <Card className="p-4" data-testid="prod-settings-report">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><Mail className="h-4 w-4" /> Daily report</div>
              {!reportCfg ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-400" /> : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-700 p-3">
                    <div>
                      <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Email automatically every weekday morning</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="prod-report-schedule-note">Sends at 7:45am MDT (America/Edmonton), Monday to Friday, covering the previous working day — Monday's covers Friday</div>
                    </div>
                    <Switch checked={!!reportCfg.config.enabled} onCheckedChange={(v) => setReportCfg((c) => ({ ...c, config: { ...c.config, enabled: v } }))} data-testid="prod-report-enabled" />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-600 dark:text-slate-300">Team</Label>
                      <select value={reportCfg.config.team} onChange={(e) => setReportCfg((c) => ({ ...c, config: { ...c.config, team: e.target.value } }))} className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm" data-testid="prod-report-team">
                        {(reportCfg.teams || teamOptions).map((tm) => <option key={tm} value={tm}>{tm}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-600 dark:text-slate-300">Window</Label>
                      <select value={reportCfg.config.days} onChange={(e) => setReportCfg((c) => ({ ...c, config: { ...c.config, days: Number(e.target.value) } }))} className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm" data-testid="prod-report-days">
                        {[1, 7, 14, 30].map((d) => <option key={d} value={d}>{`Last ${d} day${d > 1 ? "s" : ""}`}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Recipients (comma separated)</Label>
                    <Textarea value={recipInput} onChange={(e) => setRecipInput(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="reo@unitedcloud.ca" data-testid="prod-report-recipients" />
                  </div>
                  {reportCfg.last_send && (
                    <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="prod-report-last">Last send: {reportCfg.last_send.sent_at?.slice(0, 16).replace("T", " ")} UTC · {reportCfg.last_send.status} · {reportCfg.last_send.recipients?.length || 0} recipient(s)</div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={sendReportNow} disabled={reportBusy} data-testid="prod-report-send-now">{reportBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />} Send now</Button>
                    <Button size="sm" onClick={saveReport} disabled={reportBusy} data-testid="prod-report-save">Save</Button>
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4" data-testid="prod-settings-access">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><UsersIcon className="h-4 w-4" /> Access</div>
              {!acl ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-400" /> : (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500 dark:text-slate-400">Owners (can manage this list): <b>{(acl.owners || []).join(", ")}</b></div>
                  {acl.can_manage ? (
                    <>
                      <div>
                        <Label className="text-xs text-slate-600 dark:text-slate-300">Admins — can classify &amp; manage the report</Label>
                        <Textarea value={adminInput} onChange={(e) => setAdminInput(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="name@unitedcloud.ca" data-testid="prod-acl-admins" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600 dark:text-slate-300">Viewers — read-only access</Label>
                        <Textarea value={viewerInput} onChange={(e) => setViewerInput(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="name@unitedcloud.ca" data-testid="prod-acl-viewers" />
                      </div>
                      <Button size="sm" onClick={saveAcl} disabled={aclBusy} data-testid="prod-acl-save">{aclBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save access</Button>
                    </>
                  ) : (
                    <div className="rounded bg-slate-50 dark:bg-slate-800/60 p-2 text-xs text-slate-500 dark:text-slate-400">Only Dan or Keith can add admins and viewers.</div>
                  )}
                </div>
              )}
            </Card>

            <Card className="p-4" data-testid="prod-settings-idle">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><BarChart3 className="h-4 w-4" /> On-call idle allowance</div>
              {!idleCfg ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-400" /> : (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Minutes of idle time per day credited back as productive, so on-call staff aren't dinged for waiting between calls.</p>
                  <div className="flex flex-wrap gap-4">
                    {(idleCfg.teams || []).map((tm) => (
                      <div key={tm} className="flex items-center gap-2">
                        <Label className="text-xs text-slate-600 dark:text-slate-300 w-32">{tm}</Label>
                        <input type="number" min={0} max={480} value={idleCfg.minutes[tm] ?? 0}
                          onChange={(e) => setIdleCfg((c) => ({ ...c, minutes: { ...c.minutes, [tm]: Number(e.target.value) } }))}
                          className="w-20 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm" data-testid={`prod-idle-${tm.replace(/\s+/g, "-").toLowerCase()}`} />
                        <span className="text-xs text-slate-400 dark:text-slate-400">min/day</span>
                      </div>
                    ))}
                  </div>
                  <Button size="sm" onClick={saveIdle} disabled={idleBusy} data-testid="prod-idle-save">{idleBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save allowance</Button>
                </div>
              )}
            </Card>

            <Card className="p-4" data-testid="prod-settings-weekly">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><Mail className="h-4 w-4" /> Weekly manager digest</div>
              {!weeklyCfg ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-400" /> : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-700 p-3">
                    <div>
                      <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Email a per-team roll-up every Monday</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">Mondays 8:00am MDT · who hit the 7.5h goal and who trended down vs last week</div>
                    </div>
                    <Switch checked={!!weeklyCfg.config.enabled} onCheckedChange={(v) => setWeeklyCfg((c) => ({ ...c, config: { ...c.config, enabled: v } }))} data-testid="prod-weekly-enabled" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Recipients (comma separated)</Label>
                    <Textarea value={weeklyRecip} onChange={(e) => setWeeklyRecip(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="reo@unitedcloud.ca" data-testid="prod-weekly-recipients" />
                  </div>
                  {weeklyCfg.last_send && (
                    <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="prod-weekly-last">Last send: {weeklyCfg.last_send.sent_at?.slice(0, 16).replace("T", " ")} UTC · {weeklyCfg.last_send.status}</div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={sendWeeklyNow} disabled={weeklyBusy} data-testid="prod-weekly-send-now">{weeklyBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />} Send now</Button>
                    <Button size="sm" onClick={saveWeekly} disabled={weeklyBusy} data-testid="prod-weekly-save">Save</Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="prod-user-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detail?.name} <span className="text-sm font-normal text-slate-400 dark:text-slate-400">· {detail?.team}</span>
              {detail && <GoalPct pct={detail.goal_pct} />}
              {detail && <a href={detail.portal_url} target="_blank" rel="noreferrer" className="text-slate-400 dark:text-slate-400 hover:text-slate-700" title="Open in Productivity App"><ExternalLink className="h-4 w-4" /></a>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-4 text-xs text-slate-600 dark:text-slate-300">
            <span>Total work time: <b>{detail?.total_work_hms}</b></span>
            <span>Productive: <b>{detail?.productive_hms}</b></span>
            <span>Avg/day: <b>{detail?.avg_productive_hms}</b> (goal 7.5h)</span>
            <span>Idle: {detail?.idle_hms}</span>
            {detail?.streak && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${detail.streak.current > 0
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}
                title={`Consecutive tracked days at or above the ${detail.goal_hours}h goal. Days with no activity at all (e.g. weekends) are skipped, not counted as a miss.`}
                data-testid="prod-streak-badge">
                <Flame className="h-3 w-3" />
                {detail.streak.current > 0
                  ? `${detail.streak.current}-day goal streak`
                  : "No current streak"}
                <span className="font-normal text-slate-500 dark:text-slate-400">
                  · best {detail.streak.best} · {detail.streak.days_hit}/{detail.streak.days_tracked} days hit
                </span>
              </span>
            )}
          </div>

          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2" data-testid="prod-trend-chart">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-400">4-week trajectory — avg productive hours/day (goal 7.5h)</div>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={detail?.trend_weeks || []} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
                  <XAxis dataKey="label" tick={axisTick()} tickLine={false} />
                  <YAxis tick={axisTick()} width={30} domain={[0, "dataMax + 1"]} />
                  <RTooltip contentStyle={tooltipStyle()} formatter={(v) => [`${v}h/day`, "avg productive"]} />
                  <ReferenceLine y={detail?.goal_hours || 7.5} stroke="#e11d48" strokeWidth={1.5} strokeDasharray="4 3"
                    label={{ value: `${detail?.goal_hours || 7.5}h goal`, fontSize: 9, fill: "#e11d48", position: "insideTopRight" }} />
                  <Bar dataKey="avg_hours" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="avg_hours" position="top" fontSize={9} formatter={(v) => `${v}h`} />
                    {(detail?.trend_weeks || []).map((w, i) => <Cell key={i} fill={w.avg_hours >= (detail?.goal_hours || 7.5) ? "#16a34a" : "#f59e0b"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2" data-testid="prod-user-daily-chart">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-400">
              Productive hours per day — red line is the {detail?.goal_hours || 7.5}h goal
            </div>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(detail?.daily || []).map((d) => ({ ...d, hours: hrs(d.productive) }))}
                  margin={{ top: 14, right: 8, bottom: 0, left: 4 }}>
                  <XAxis dataKey="date" tick={axisTick()} tickLine={false} interval="preserveStartEnd"
                    tickFormatter={(d) => String(d).slice(5)} />
                  <YAxis tick={axisTick()} width={30}
                    domain={[0, (m) => Math.max(m + 1, (detail?.goal_hours || 7.5) + 1)]} />
                  <RTooltip contentStyle={tooltipStyle()} formatter={(v) => [`${v}h`, "productive"]} />
                  <ReferenceLine y={detail?.goal_hours || 7.5} stroke="#e11d48" strokeWidth={1.5} strokeDasharray="4 3"
                    label={{ value: `${detail?.goal_hours || 7.5}h goal`, fontSize: 9, fill: "#e11d48", position: "insideTopRight" }} />
                  <Bar dataKey="hours" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="hours" position="top" fontSize={9} formatter={(v) => `${v}h`} />
                    {(detail?.daily || []).map((d, i) => (
                      <Cell key={i} fill={hrs(d.productive) >= (detail?.goal_hours || 7.5) ? "#16a34a" : "#f59e0b"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-3 py-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Timecard</div>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 dark:text-slate-400">
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-600" />productive</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-500" />other</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-rose-600" />unproductive</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-slate-300" />idle</span>
                <span className="inline-flex items-center gap-1"><span className="h-2.5 w-0.5 bg-slate-900" />7.5h goal</span>
              </div>
            </div>
            <div className="max-h-56 overflow-auto" data-testid="prod-timecard">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr><th className="px-3 py-1.5">Date</th><th className="px-3 py-1.5" title="First and last activity recorded at the machine that day">Shift</th><th className="px-3 py-1.5 text-right">Work time</th><th className="px-3 py-1.5 text-right" title="Productive hours that day against the 7.5h goal">Productive</th><th className="px-3 py-1.5 w-1/3">Time split</th><th className="px-3 py-1.5"></th></tr>
                </thead>
                <tbody>
                  {(detail?.timecard || []).map((row) => (
                    <Fragment key={row.date}>
                    <tr className="cursor-pointer border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60" onClick={() => openDay(row.date)} data-testid={`prod-timecard-row-${row.date}`}>
                      <td className="px-3 py-1.5">
                        <span className="inline-flex items-center gap-1">
                          <ChevronsUpDown className="h-3 w-3 text-slate-400 dark:text-slate-400" />
                          <span className="font-medium text-slate-800 dark:text-slate-100">{row.date}</span>
                          {row.tag && <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700" data-testid={`prod-day-tag-${row.date}`}>{row.tag}</span>}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs text-slate-500 dark:text-slate-400" data-testid={`prod-timecard-shift-${row.date}`}>
                        {row.shift_start ? `${row.shift_start}–${row.shift_end}` : "—"}
                        {row.out_window_seconds > 300 && (
                          <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400"
                                title="Activity recorded outside this person's working window — counted, but flagged">
                            +{hrs(row.out_window_seconds)}h out
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs">{row.work_hms}</td>
                      <td className={`px-3 py-1.5 text-right font-mono text-xs font-semibold ${row.productive_seconds >= (detail?.goal_seconds || 27000) ? "text-emerald-700 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}
                        data-testid={`prod-timecard-prod-${row.date}`}>
                        {hrs(row.productive_seconds)}h{row.goal_hit ? " ✓" : ""}
                      </td>
                      <td className="px-3 py-1.5"><TimecardBar row={row} /></td>
                      <td className="px-3 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <a href={row.recording_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800" title="Open recordings in Productivity App" data-testid={`prod-rec-${row.date}`}>
                          <Video className="h-3.5 w-3.5" /> Recordings
                        </a>
                      </td>
                    </tr>
                    {dayOpen === row.date && (
                      <tr key={`${row.date}-apps`} className="bg-slate-50 dark:bg-slate-800/60" data-testid={`prod-day-apps-${row.date}`}>
                        <td colSpan={6} className="px-4 py-2">
                          {dayBusy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400 dark:text-slate-400" /> : (
                            dayApps.length === 0 ? <span className="text-xs text-slate-400 dark:text-slate-400">No apps recorded this day.</span> : (
                              <div className="space-y-0.5">
                                {dayBands && dayBands.length > 0 && (
                                  <div className="mb-2" data-testid={`prod-day-band-${row.date}`}>
                                    <DayBandChart bands={dayBands} window={dayBandWindow} testid={`prod-day-bandchart-${row.date}`} />
                                    {dayLongest && dayLongest.seconds >= 900 && (
                                      <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                                        Longest break {dayLongest.hms} @ {dayLongest.start}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-400">Apps open on {row.date} (idle {row.idle_hms} of {hrs(row.machine_work || row.work_seconds)}h at the machine)</div>
                                {dayApps.slice(0, 12).map((a) => (
                                  <div key={`${a.name}-${a.is_app ? "app" : "site"}`} className="flex items-center justify-between text-xs">
                                    <span className="truncate">{a.name} <ClsBadge cls={a.cls} /></span>
                                    <span className="font-mono text-slate-500 dark:text-slate-400">{a.hms}<span className="ml-2 text-slate-400 dark:text-slate-400">idle {a.idle_hms}</span></span>
                                  </div>
                                ))}
                                {isManager && (
                                  <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-200 dark:border-slate-700 pt-2" onClick={(e) => e.stopPropagation()}>
                                    <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-400">Tag this day:</span>
                                    <select value={dayTag} onChange={(e) => setDayTag(e.target.value)} className="rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs" data-testid={`prod-day-tag-select-${row.date}`}>
                                      {["", "on-call", "PTO", "meeting", "training", "sick", "other"].map((tg) => <option key={tg} value={tg}>{tg || "(none)"}</option>)}
                                    </select>
                                    <input value={dayNote} onChange={(e) => setDayNote(e.target.value)} placeholder="note (optional)" className="flex-1 min-w-[140px] rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-xs" data-testid={`prod-day-note-${row.date}`} />
                                    <Button size="sm" variant="outline" onClick={saveDayNote} data-testid={`prod-day-note-save-${row.date}`}>Save tag</Button>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-1 text-[10px] text-slate-400 dark:text-slate-400">Shift shows the first and last activity recorded at the machine that day; "out" marks work outside the expected window. Recording links open the monitoring tool.</div>
          </div>

          <div className="max-h-56 overflow-auto">
            <div className="flex items-center justify-between px-1 pb-1 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-400">
              <span>Application / website</span>
              <div className="flex items-center gap-1 normal-case">
                <span>sort:</span>
                <select value={itemSort} onChange={(e) => setItemSort(e.target.value)} className="rounded border border-slate-200 dark:border-slate-700 px-1 py-0.5 text-[11px]" data-testid="prod-item-sort">
                  <option value="time">% of goal</option>
                  <option value="cls">classification</option>
                  <option value="name">name</option>
                </select>
              </div>
            </div>
            {sortedItems.map((i) => (
              <div key={i.name} className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 py-1 text-sm" data-testid={`prod-item-${i.name}`}>
                <span className="w-1/2 truncate">{i.name} <ClsBadge cls={i.cls} /></span>
                <div className="flex flex-1 items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, i.goal_pct)}%` }} />
                  </div>
                  <span className="w-10 text-right text-xs font-semibold text-slate-600 dark:text-slate-300" data-testid={`prod-item-pct-${i.name}`}>{i.goal_pct}%</span>
                  <span className="w-14 text-right font-mono text-xs text-slate-400 dark:text-slate-400">{i.hms}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => downloadUserPdf(detail.key)} data-testid="prod-detail-pdf"><FileDown className="mr-1 h-4 w-4" /> Download user report</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
